import { Descriptions, Drawer, Space, Tag, Typography } from 'antd'
import type { SurveyResponseItem, SurveyQuestion, SurveyStepAnswer } from '../types/survey.types'

const { Text } = Typography

interface SurveyResponseDetailProps {
  response: SurveyResponseItem | null
  questions: SurveyQuestion[]
  open: boolean
  onClose: () => void
}

function renderAnswer(answer: SurveyStepAnswer | undefined, question: SurveyQuestion) {
  if (!answer) return <Text type="secondary">No response</Text>

  if (question.type === 'multi-select') {
    const hasValues = answer.values && answer.values.length > 0
    if (!hasValues && !answer.otherText) return <Text type="secondary">No response</Text>
    return (
      <>
        {answer.values?.map(item => (
          <Tag key={item} style={{ marginBottom: 4 }}>
            {item}
          </Tag>
        ))}
        {answer.otherText && (
          <div style={{ marginTop: 4 }}>
            <Text type="secondary">Other: </Text>
            {answer.otherText}
          </div>
        )}
      </>
    )
  }

  if (question.type === 'single-select') {
    return answer.value ? <Tag color="blue">{answer.value}</Tag> : <Text type="secondary">No response</Text>
  }

  if (question.type === 'text') {
    return answer.text ? <Text>{answer.text}</Text> : <Text type="secondary">No response</Text>
  }

  return <Text type="secondary">No response</Text>
}

export function SurveyResponseDetail({ response, questions, open, onClose }: SurveyResponseDetailProps) {
  if (!response) return null

  const sortedQuestions = [...questions].sort((a, b) => a.step - b.step)

  return (
    <Drawer title="Survey Response Detail" open={open} onClose={onClose} width={500}>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="User">{response.userEmail || response.userId}</Descriptions.Item>
        <Descriptions.Item label="Tenant">{response.tenantName || response.tenantCode}</Descriptions.Item>
        <Descriptions.Item label="Status">
          {response.isCompleted ? (
            <Tag color="success">Completed</Tag>
          ) : response.isDismissed ? (
            <Tag color="warning">Dismissed</Tag>
          ) : response.currentStep > 0 ? (
            <Tag color="processing">
              In Progress (Step {response.currentStep}/{sortedQuestions.length})
            </Tag>
          ) : (
            <Tag>Not Started</Tag>
          )}
        </Descriptions.Item>
        {response.submittedAt && (
          <Descriptions.Item label="Submitted">{new Date(response.submittedAt).toLocaleString()}</Descriptions.Item>
        )}
      </Descriptions>

      <div style={{ marginTop: 24 }}>
        {sortedQuestions.map(q => {
          const answer = response.answers?.[String(q.step)]
          return (
            <div key={q.step} style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Q{q.step}: {q.label}
              </Text>
              <Space size={[4, 4]} wrap>
                {renderAnswer(answer, q)}
              </Space>
            </div>
          )
        })}
      </div>
    </Drawer>
  )
}
